import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Tag, Select, Input, Card, Space, Typography, DatePicker,
    Button, Tooltip, Alert, Spin, Row, Col, Statistic, Dropdown, message
} from 'antd';
import {
    SearchOutlined, ReloadOutlined, UserOutlined, CheckCircleOutlined,
    CloseCircleOutlined, AuditOutlined, TeamOutlined, WarningOutlined,
    DownloadOutlined, FileExcelOutlined, FilePdfOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SideBar from '../../components/SideBar';
import NavBar from '../../components/NavBar';
import Footer from '../../components/Footer';
import axiosInstance from '../../variables/api/axiosInstance';

// Plafond de sécurité pour l'export (une seule requête ramène jusqu'à ce nombre de
// lignes filtrées). Au-delà, on exporte les plus récentes et on prévient l'utilisateur.
const EXPORT_MAX = 20000;

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option, OptGroup } = Select;

const colors = {
    primary: '#242166',
    secondary: '#3B82F6',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    background: '#F8FAFC',
    cardBg: '#FFFFFF',
    border: '#E2E8F0',
    text: '#334155',
    textLight: '#64748B',
};

const headerStyle = {
    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
    padding: '32px 40px',
    borderRadius: '16px 16px 0 0',
};

const cardStyle = {
    borderRadius: '16px',
    boxShadow: '0 10px 40px rgba(36, 33, 102, 0.1)',
    border: 'none',
    overflow: 'hidden',
};

const filterSectionStyle = {
    background: colors.cardBg,
    padding: '24px 40px',
    borderBottom: `1px solid ${colors.border}`,
};

const ACTION_FAMILIES = {
    AUTH: { color: '#1677ff', bg: '#e6f4ff', label: 'Auth' },
    CONTRACT: { color: '#531dab', bg: '#f9f0ff', label: 'Contrat' },
    SINISTRE: { color: '#d46b08', bg: '#fff7e6', label: 'Sinistre' },
    USER: { color: '#389e0d', bg: '#f6ffed', label: 'Utilisateur' },
    COMPTA: { color: '#006d75', bg: '#e6fffb', label: 'Comptabilité' },
    REGLEMENT: { color: '#006d75', bg: '#e6fffb', label: 'Règlement' },
    BORDEREAU: { color: '#08979c', bg: '#e6fffb', label: 'Bordereau' },
    CESSION: { color: '#9254de', bg: '#f9f0ff', label: 'Cession' },
    RETROCESSION: { color: '#c41d7f', bg: '#fff0f6', label: 'Rétrocession' },
    MIGRATION: { color: '#595959', bg: '#f5f5f5', label: 'Migration' },
    OTHER: { color: '#8c8c8c', bg: '#f5f5f5', label: 'Autre' },
    PARAMS: { color: '#7c3aed', bg: '#ede9fe', label: 'Paramètres' },
    COURTIER: { color: '#d4380d', bg: '#fff2e8', label: 'Courtier' },
    LOT: { color: '#1d39c4', bg: '#f0f5ff', label: 'Lots attestations' },
};

const getActionFamily = (actionType) => {
    if (!actionType) return 'OTHER';
    if (actionType.startsWith('AUTH_')) return 'AUTH';
    if (actionType.startsWith('CONTRACT_')) return 'CONTRACT';
    if (actionType.startsWith('AVENANT_')) return 'CONTRACT';
    if (actionType.startsWith('ATTESTATION_')) return 'CONTRACT';
    if (actionType.startsWith('COURTIER_') || actionType === 'TAUX_COMMISSION_ASSIGN' || actionType === 'COMMISSION_RECALC' || actionType === 'RETENUE_FISCALE_MODIFICATION') return 'COURTIER';
    if (actionType.startsWith('LOT_ATTESTATION_') || actionType.startsWith('DISPATCHED_LOT_')) return 'LOT';
    if (actionType.startsWith('SINISTRE_')) return 'SINISTRE';
    if (actionType.startsWith('USER_')) return 'USER';
    if (actionType.startsWith('COMPTA_')) return 'COMPTA';
    if (actionType.startsWith('REGLEMENT_')) return 'REGLEMENT';
    if (actionType.startsWith('BORDEREAU_')) return 'BORDEREAU';
    if (actionType.startsWith('CESSION_')) return 'CESSION';
    if (actionType.startsWith('RETROCESSION_')) return 'RETROCESSION';
    if (actionType.startsWith('MIGRATION_')) return 'MIGRATION';
    if (actionType.startsWith('PARAMS_')) return 'PARAMS';
    return 'OTHER';
};

const ACTION_LABELS_FR = {
    AUTH_LOGIN: 'Connexion',
    AUTH_LOGOUT: 'Déconnexion',
    AUTH_LOGIN_FAILED: 'Échec connexion',
    AUTH_OTP_SENT: 'OTP envoyé',
    AUTH_OTP_VERIFIED: 'OTP vérifié',
    AUTH_OTP_FAILED: 'Échec OTP',
    AUTH_PASSWORD_CHANGE: 'Changement MDP',
    AUTH_PASSWORD_RESET: 'Réinit. MDP',
    CONTRACT_CREATE: 'Nouveau contrat',
    CONTRACT_UPDATE: 'Modif. contrat',
    CONTRACT_DELETE: 'Suppr. contrat',
    CONTRACT_PRINT: 'Impression contrat',
    CONTRACT_CP_PRINT: 'Impression CP',
    CONTRACT_PROPOSITION_PRINT: 'Impression proposition',
    ATTESTATION_PRINT: 'Impression attestation',
    ATTESTATION_REPRINT_DAMAGED: 'Réimpr. attest. (bourrée)',
    ATTESTATION_REPRINT_NONPRINTED: 'Réimpr. attest. (non sortie)',
    CONTRACT_AVENANT: 'Avenant',
    CONTRACT_SUSPEND: 'Suspension',
    CONTRACT_REACTIVATE: 'Réactivation',
    CONTRACT_RESILIATION: 'Résiliation',
    CONTRACT_RENEW: 'Renouvellement',
    CONTRACT_PROROGATION: 'Prorogation',
    CONTRACT_INTERMEDIAIRE_CHANGE: "Changement d'intermédiaire",
    CONTRACT_RETRAIT_VEHICULE: 'Retrait véhicule',
    CONTRACT_ANNULATION: 'Annulation',
    CONTRACT_ACCESSOIRES_EDIT: 'Édition frais accessoires',
    CONTRACT_BONUS_EDIT: 'Édition taux bonus/malus',
    CONTRACT_ANTIDATE: 'Contrat antidaté',
    AVENANT_ANNULE: 'Avenant annulé',
    CONTRACT_AGENCE_COMMISSION: 'Agence ciblée / commission',
    SINISTRE_CREATE: 'Nouveau sinistre',
    SINISTRE_UPDATE: 'Modif. sinistre',
    SINISTRE_DELETE: 'Suppr. sinistre',
    SINISTRE_CLOTURE: 'Clôture sinistre',
    SINISTRE_SUSPENSION: 'Suspension sinistre',
    SINISTRE_DECLARATION: 'Déclaration',
    SINISTRE_EVALUATION: 'Évaluation',
    SINISTRE_DECAISSEMENT: 'Décaissement',
    EXPERT_AFFECTATION: 'Affectation expert',
    EXPERTISE_CREATE: 'Nouvelle expertise',
    EXPERTISE_UPDATE: 'Modif. expertise',
    PEC_CREATE: 'Nouvelle prise en charge',
    PEC_UPDATE: 'Modif. prise en charge',
    PEC_DELETE: 'Suppr. prise en charge',
    OFFRE_CREATE: 'Nouvelle offre',
    OFFRE_UPDATE: 'Modif. offre',
    VICTIME_ADD: 'Ajout victime',
    SINISTRE_DOCUMENT_UPLOAD: 'Dépôt document',
    SINISTRE_DOCUMENT_DOWNLOAD: 'Téléchargement document',
    USER_CREATE: 'Nouvel utilisateur',
    USER_UPDATE: 'Modif. utilisateur',
    USER_DELETE: 'Suppr. utilisateur',
    USER_ACTIVATE: 'Activation',
    USER_DEACTIVATE: 'Désactivation',
    PARAMS_CREATE: 'Nouveau paramètre',
    PARAMS_UPDATE: 'Modif. paramètre',
    PARAMS_DELETE: 'Suppr. paramètre',
    COMPTA_ECRITURE: 'Écriture comptable',
    COMPTA_CORRECTION: 'Correction compta.',
    REGLEMENT_CREATE: 'Encaissement',
    REGLEMENT_ENCAISSE: 'Encaissement',
    REGLEMENT_ANNULE: 'Annul. règlement',
    BORDEREAU_REVERSEMENT_CREATE: 'Bordereau reversement',
    BORDEREAU_COMMISSION_CREATE: 'Bordereau commissions',
    BORDEREAU_REGLEMENT: 'Règlement bordereau',
    BORDEREAU_MODE_PAIEMENT_EDIT: 'Modif. mode règlement',
    BORDEREAU_DELETE: 'Suppr. bordereau',
    CESSION_CREATE: 'Nouvelle cession',
    CESSION_UPDATE: 'Modif. cession',
    CESSION_DELETE: 'Suppr. cession',
    CESSION_VALIDER_REGLEMENT: 'Valid. règl. cession',
    RETROCESSION_CREATE: 'Nouvelle rétrocession',
    RETROCESSION_UPDATE: 'Modif. rétrocession',
    RETROCESSION_PAIEMENT: 'Paiement rétrocession',
    RETROCESSION_DELETE: 'Suppr. rétrocession',
    COURTIER_CREATE: 'Nouveau courtier',
    COURTIER_VALIDATE: 'Validation courtier',
    COMMISSION_RECALC: 'Recalcul commissions',
    TAUX_COMMISSION_ASSIGN: 'Attribution taux commission',
    RETENUE_FISCALE_MODIFICATION: 'Taux de retenue fiscale',
    LOT_ATTESTATION_CREATE: 'Nouveau lot attestations',
    DISPATCHED_LOT_CREATE: 'Distribution de lot',
    DISPATCHED_LOT_CANCEL: 'Annul. distribution de lot',
    MIGRATION_IMPORT: 'Import migration',
    OTHER: 'Autre',
};

const ACTION_TYPE_GROUPS = [
    {
        label: 'Authentification',
        family: 'AUTH',
        values: ['AUTH_LOGIN', 'AUTH_LOGOUT', 'AUTH_LOGIN_FAILED', 'AUTH_OTP_SENT', 'AUTH_OTP_VERIFIED', 'AUTH_OTP_FAILED', 'AUTH_PASSWORD_CHANGE', 'AUTH_PASSWORD_RESET'],
    },
    {
        label: 'Contrats',
        family: 'CONTRACT',
        values: ['CONTRACT_CREATE', 'CONTRACT_UPDATE', 'CONTRACT_DELETE', 'CONTRACT_PRINT', 'CONTRACT_CP_PRINT', 'CONTRACT_PROPOSITION_PRINT', 'ATTESTATION_PRINT', 'ATTESTATION_REPRINT_DAMAGED', 'ATTESTATION_REPRINT_NONPRINTED', 'CONTRACT_AVENANT', 'CONTRACT_SUSPEND', 'CONTRACT_REACTIVATE', 'CONTRACT_RESILIATION', 'CONTRACT_RENEW', 'CONTRACT_PROROGATION', 'CONTRACT_RETRAIT_VEHICULE', 'CONTRACT_ANNULATION', 'CONTRACT_ACCESSOIRES_EDIT', 'CONTRACT_BONUS_EDIT', 'CONTRACT_ANTIDATE', 'CONTRACT_AGENCE_COMMISSION', 'AVENANT_ANNULE'],
    },
    {
        label: 'Sinistres',
        family: 'SINISTRE',
        values: ['SINISTRE_CREATE', 'SINISTRE_UPDATE', 'SINISTRE_DELETE', 'SINISTRE_CLOTURE', 'SINISTRE_SUSPENSION', 'SINISTRE_DECLARATION', 'SINISTRE_EVALUATION', 'SINISTRE_DECAISSEMENT', 'EXPERT_AFFECTATION', 'EXPERTISE_CREATE', 'EXPERTISE_UPDATE', 'PEC_CREATE', 'PEC_UPDATE', 'PEC_DELETE', 'OFFRE_CREATE', 'OFFRE_UPDATE', 'VICTIME_ADD', 'SINISTRE_DOCUMENT_UPLOAD', 'SINISTRE_DOCUMENT_DOWNLOAD'],
    },
    {
        label: 'Utilisateurs',
        family: 'USER',
        values: ['USER_CREATE', 'USER_UPDATE', 'USER_DELETE', 'USER_ACTIVATE', 'USER_DEACTIVATE'],
    },
    {
        label: 'Paramètres',
        family: 'PARAMS',
        values: ['PARAMS_CREATE', 'PARAMS_UPDATE', 'PARAMS_DELETE'],
    },
    {
        label: 'Comptabilité',
        family: 'COMPTA',
        values: ['COMPTA_ECRITURE', 'COMPTA_CORRECTION'],
    },
    {
        label: 'Règlements',
        family: 'REGLEMENT',
        values: ['REGLEMENT_CREATE', 'REGLEMENT_ENCAISSE', 'REGLEMENT_ANNULE'],
    },
    {
        label: 'Bordereaux',
        family: 'BORDEREAU',
        values: ['BORDEREAU_REVERSEMENT_CREATE', 'BORDEREAU_COMMISSION_CREATE', 'BORDEREAU_REGLEMENT', 'BORDEREAU_MODE_PAIEMENT_EDIT', 'BORDEREAU_DELETE'],
    },
    {
        label: 'Cession',
        family: 'CESSION',
        values: ['CESSION_CREATE', 'CESSION_UPDATE', 'CESSION_DELETE', 'CESSION_VALIDER_REGLEMENT'],
    },
    {
        label: 'Rétrocession',
        family: 'RETROCESSION',
        values: ['RETROCESSION_CREATE', 'RETROCESSION_UPDATE', 'RETROCESSION_PAIEMENT', 'RETROCESSION_DELETE'],
    },
    {
        label: 'Courtiers',
        family: 'COURTIER',
        values: ['COURTIER_CREATE', 'COURTIER_VALIDATE', 'TAUX_COMMISSION_ASSIGN', 'RETENUE_FISCALE_MODIFICATION'],
    },
    {
        label: 'Lots attestations',
        family: 'LOT',
        values: ['LOT_ATTESTATION_CREATE', 'DISPATCHED_LOT_CREATE', 'DISPATCHED_LOT_CANCEL'],
    },
    {
        label: 'Migration',
        family: 'MIGRATION',
        values: ['MIGRATION_IMPORT'],
    },
];

const ActionTag = ({ actionType }) => {
    const family = getActionFamily(actionType);
    const style = ACTION_FAMILIES[family] || ACTION_FAMILIES.OTHER;
    const label = ACTION_LABELS_FR[actionType] || actionType;
    return (
        <Tag
            style={{
                background: style.bg,
                border: `1px solid ${style.color}30`,
                color: style.color,
                fontWeight: 600,
                fontSize: '11px',
                padding: '2px 8px',
                borderRadius: '6px',
                letterSpacing: '0.3px',
            }}
        >
            {label || '—'}
        </Tag>
    );
};

const ActivityLogs = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState('');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 });
    const [stats, setStats] = useState({ today: 0, failuresToday: 0, activeUsersToday: 0 });
    const [filters, setFilters] = useState({
        actionTypes: [],
        utilisateurCodeId: '',
        dateRange: [null, null],
        success: undefined,
        search: '',
    });

    const fetchStats = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get('/api/activity-log/stats');
            setStats(data || {});
        } catch (_) { }
    }, []);

    const buildParams = useCallback((page = 0, size = 50) => {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('size', size);
        if (filters.actionTypes?.length) {
            filters.actionTypes.forEach(a => params.append('actionTypes', a));
        }
        if (filters.utilisateurCodeId?.trim()) params.append('utilisateurCodeId', filters.utilisateurCodeId.trim());
        if (filters.dateRange?.[0]) params.append('dateFrom', filters.dateRange[0].format('YYYY-MM-DD'));
        if (filters.dateRange?.[1]) params.append('dateTo', filters.dateRange[1].format('YYYY-MM-DD'));
        if (filters.success !== undefined && filters.success !== null) params.append('success', filters.success);
        if (filters.search?.trim()) params.append('search', filters.search.trim());
        return params.toString();
    }, [filters]);

    const fetchLogs = useCallback(async (page = 0, size = 50) => {
        setLoading(true);
        setError('');
        try {
            const query = buildParams(page, size);
            const { data } = await axiosInstance.get(`/api/activity-log?${query}`);
            const pageData = data?.content ?? [];
            setLogs(pageData);
            setPagination(prev => ({
                ...prev,
                current: (data?.number ?? 0) + 1,
                pageSize: data?.size ?? size,
                total: data?.totalElements ?? 0,
            }));
        } catch (_) {
            setError('Impossible de charger les logs d\'activité. Veuillez réessayer.');
        } finally {
            setLoading(false);
        }
    }, [buildParams]);

    useEffect(() => {
        fetchLogs(0, pagination.pageSize);
        fetchStats();
    }, []);

    const handleTableChange = (pag) => {
        fetchLogs(pag.current - 1, pag.pageSize);
    };

    const handleSearch = () => {
        fetchLogs(0, pagination.pageSize);
    };

    const handleReset = () => {
        setFilters({ actionTypes: [], utilisateurCodeId: '', dateRange: [null, null], success: undefined, search: '' });
        setTimeout(() => fetchLogs(0, pagination.pageSize), 0);
    };

    // Récupère TOUTES les lignes correspondant aux filtres courants (pas seulement la
    // page affichée), en une seule requête plafonnée à EXPORT_MAX.
    const fetchAllForExport = useCallback(async () => {
        const size = Math.min(Math.max(pagination.total || pagination.pageSize, 1), EXPORT_MAX);
        const query = buildParams(0, size);
        const { data } = await axiosInstance.get(`/api/activity-log?${query}`);
        return { rows: data?.content ?? [], total: data?.totalElements ?? 0 };
    }, [buildParams, pagination.total, pagination.pageSize]);

    const exportRows = (rows) => rows.map((r) => ({
        'Date / Heure': r.createdAt ? dayjs(r.createdAt).format('YYYY-MM-DD HH:mm:ss') : '',
        'Utilisateur': r.nomComplet || r.username || '',
        'Organisme': r.organismeCode || '',
        'Agence': r.agenceCode || '',
        'Action': ACTION_LABELS_FR[r.actionType] || r.actionType || '',
        'Description': r.description || '',
        'IP': r.ipAddress || '',
        'Succès': r.success ? 'Oui' : 'Non',
    }));

    const warnIfTruncated = (rows, total) => {
        if (total > rows.length) {
            message.warning(
                `Export limité aux ${rows.length} lignes les plus récentes (sur ${total}). Affinez la période pour tout exporter.`
            );
        }
    };

    const handleExportExcel = async () => {
        setExporting(true);
        try {
            const { rows, total } = await fetchAllForExport();
            if (!rows.length) { message.info('Aucune activité à exporter'); return; }
            const ws = XLSX.utils.json_to_sheet(exportRows(rows));
            ws['!cols'] = [{ wch: 20 }, { wch: 24 }, { wch: 12 }, { wch: 12 }, { wch: 24 }, { wch: 50 }, { wch: 16 }, { wch: 8 }];
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Journal d'activité");
            XLSX.writeFile(wb, `journal-activite-${dayjs().format('YYYYMMDD_HHmm')}.xlsx`);
            warnIfTruncated(rows, total);
        } catch (_) {
            message.error("Échec de l'export Excel");
        } finally {
            setExporting(false);
        }
    };

    const handleExportPdf = async () => {
        setExporting(true);
        try {
            const { rows, total } = await fetchAllForExport();
            if (!rows.length) { message.info('Aucune activité à exporter'); return; }
            const doc = new jsPDF({ orientation: 'landscape' });
            doc.setFontSize(14);
            doc.text("Journal d'activité", 14, 15);
            doc.setFontSize(9);
            doc.text(`Généré par PoolIns le ${dayjs().format('DD/MM/YYYY HH:mm')} — ${rows.length} entrée(s)`, 14, 21);
            autoTable(doc, {
                startY: 26,
                head: [['Date / Heure', 'Utilisateur', 'Org.', 'Agence', 'Action', 'Description', 'IP', 'OK']],
                body: rows.map((r) => [
                    r.createdAt ? dayjs(r.createdAt).format('YYYY-MM-DD HH:mm:ss') : '',
                    r.nomComplet || r.username || '',
                    r.organismeCode || '',
                    r.agenceCode || '',
                    ACTION_LABELS_FR[r.actionType] || r.actionType || '',
                    r.description || '',
                    r.ipAddress || '',
                    r.success ? 'Oui' : 'Non',
                ]),
                styles: { fontSize: 7, cellPadding: 1.5, overflow: 'linebreak' },
                headStyles: { fillColor: [36, 33, 102] },
                columnStyles: { 5: { cellWidth: 95 } },
            });
            doc.save(`journal-activite-${dayjs().format('YYYYMMDD_HHmm')}.pdf`);
            warnIfTruncated(rows, total);
        } catch (_) {
            message.error("Échec de l'export PDF");
        } finally {
            setExporting(false);
        }
    };

    const exportMenuItems = [
        { key: 'excel', icon: <FileExcelOutlined style={{ color: '#217346' }} />, label: 'Exporter en Excel', onClick: handleExportExcel },
        { key: 'pdf', icon: <FilePdfOutlined style={{ color: '#d32029' }} />, label: 'Exporter en PDF', onClick: handleExportPdf },
    ];

    const columns = [
        {
            title: 'Date / Heure',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 185,
            render: (text) => (
                <Text style={{ color: colors.textLight, fontFamily: 'monospace', fontSize: '12px' }}>
                    {text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '—'}
                </Text>
            ),
        },
        {
            title: 'Utilisateur',
            key: 'utilisateur',
            width: 200,
            render: (_, record) => {
                const name = record.nomComplet || record.username || '—';
                return (
                    <Space size={4}>
                        <UserOutlined style={{ color: colors.textLight, fontSize: 12 }} />
                        <Text style={{ fontSize: '13px', color: colors.text }}>{name}</Text>
                    </Space>
                );
            },
        },
        {
            title: 'Organisme / Agence',
            key: 'organisme',
            width: 180,
            render: (_, record) => (
                <Space direction="vertical" size={2}>
                    {record.organismeCode && (
                        <Tag style={{ background: `${colors.primary}12`, border: `1px solid ${colors.primary}25`, color: colors.primary, borderRadius: '5px', fontSize: '11px', fontWeight: 600, margin: 0 }}>
                            {record.organismeCode}
                        </Tag>
                    )}
                    {record.agenceCode && (
                        <Tag style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', borderRadius: '5px', fontSize: '11px', margin: 0 }}>
                            {record.agenceCode}
                        </Tag>
                    )}
                    {!record.organismeCode && !record.agenceCode && (
                        <Text style={{ color: colors.textLight, fontSize: '12px' }}>—</Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'Action',
            dataIndex: 'actionType',
            key: 'actionType',
            width: 180,
            render: (actionType) => <ActionTag actionType={actionType} />,
        },
        // {
        //     title: 'Entité',
        //     dataIndex: 'entityType',
        //     key: 'entityType',
        //     width: 130,
        //     render: (text) => (
        //         <Tag style={{ background: `${colors.primary}10`, border: `1px solid ${colors.primary}20`, color: colors.primary, borderRadius: '6px', fontSize: '11px' }}>
        //             {text || '—'}
        //         </Tag>
        //     ),
        // },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <Text style={{ color: colors.text, fontSize: '13px' }}>{text || '—'}</Text>
                </Tooltip>
            ),
        },
        {
            title: 'IP',
            dataIndex: 'ipAddress',
            key: 'ipAddress',
            width: 140,
            render: (text) => (
                <Text style={{ color: colors.textLight, fontFamily: 'monospace', fontSize: '12px' }}>{text || '—'}</Text>
            ),
        },
        {
            title: 'Succès',
            dataIndex: 'success',
            key: 'success',
            width: 90,
            align: 'center',
            render: (success) =>
                success ? (
                    <CheckCircleOutlined style={{ color: colors.success, fontSize: 16 }} />
                ) : (
                    <CloseCircleOutlined style={{ color: colors.error, fontSize: 16 }} />
                ),
        },
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{ background: colors.background, minHeight: '100vh', padding: '40px' }}>
                                <Card style={cardStyle} bodyStyle={{ padding: 0 }}>
                                    {/* Header */}
                                    <div style={headerStyle}>
                                        <Title level={2} style={{ color: '#FFFFFF', margin: 0, marginBottom: '8px' }}>
                                            Journal d'activité
                                        </Title>
                                        <p style={{ color: '#E0E7FF', margin: 0, fontSize: '15px' }}>
                                            Audit complet des actions utilisateurs sur la plateforme
                                        </p>
                                    </div>

                                    {/* Stats */}
                                    <div style={{ padding: '24px 40px', background: colors.cardBg, borderBottom: `1px solid ${colors.border}` }}>
                                        <Row gutter={24}>
                                            <Col xs={24} sm={8}>
                                                <Card
                                                    size="small"
                                                    style={{ borderRadius: 12, border: `1px solid ${colors.border}`, background: '#f0f9ff' }}
                                                    bodyStyle={{ padding: '16px 20px' }}
                                                >
                                                    <Statistic
                                                        title={<Text style={{ color: colors.textLight, fontSize: 13 }}>Actions aujourd'hui</Text>}
                                                        value={stats.today ?? 0}
                                                        valueStyle={{ color: colors.primary, fontSize: 28, fontWeight: 700 }}
                                                        prefix={<AuditOutlined />}
                                                    />
                                                </Card>
                                            </Col>
                                            <Col xs={24} sm={8}>
                                                <Card
                                                    size="small"
                                                    style={{ borderRadius: 12, border: `1px solid ${colors.border}`, background: '#fff1f0' }}
                                                    bodyStyle={{ padding: '16px 20px' }}
                                                >
                                                    <Statistic
                                                        title={<Text style={{ color: colors.textLight, fontSize: 13 }}>Échecs aujourd'hui</Text>}
                                                        value={stats.failuresToday ?? 0}
                                                        valueStyle={{ color: colors.error, fontSize: 28, fontWeight: 700 }}
                                                        prefix={<WarningOutlined />}
                                                    />
                                                </Card>
                                            </Col>
                                            <Col xs={24} sm={8}>
                                                <Card
                                                    size="small"
                                                    style={{ borderRadius: 12, border: `1px solid ${colors.border}`, background: '#f6ffed' }}
                                                    bodyStyle={{ padding: '16px 20px' }}
                                                >
                                                    <Statistic
                                                        title={<Text style={{ color: colors.textLight, fontSize: 13 }}>Utilisateurs actifs</Text>}
                                                        value={stats.activeUsersToday ?? 0}
                                                        valueStyle={{ color: colors.success, fontSize: 28, fontWeight: 700 }}
                                                        prefix={<TeamOutlined />}
                                                    />
                                                </Card>
                                            </Col>
                                        </Row>
                                    </div>

                                    {/* Filters */}
                                    <div style={filterSectionStyle}>
                                        <Space size="middle" wrap style={{ width: '100%' }}>
                                            <Select
                                                mode="multiple"
                                                placeholder="Type d'action"
                                                style={{ minWidth: 260 }}
                                                allowClear
                                                maxTagCount={2}
                                                value={filters.actionTypes}
                                                onChange={(v) => setFilters(prev => ({ ...prev, actionTypes: v }))}
                                                size="large"
                                            >
                                                {ACTION_TYPE_GROUPS.map(group => (
                                                    <OptGroup key={group.label} label={
                                                        <span style={{ color: ACTION_FAMILIES[group.family]?.color, fontWeight: 600 }}>
                                                            {group.label}
                                                        </span>
                                                    }>
                                                        {group.values.map(v => (
                                                            <Option key={v} value={v}>{v}</Option>
                                                        ))}
                                                    </OptGroup>
                                                ))}
                                            </Select>

                                            <Input
                                                placeholder="Recherche (description, entité…)"
                                                prefix={<SearchOutlined style={{ color: colors.textLight }} />}
                                                value={filters.search}
                                                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                                onPressEnter={handleSearch}
                                                style={{ width: 280 }}
                                                size="large"
                                                allowClear
                                            />

                                            <Input
                                                placeholder="Code utilisateur"
                                                prefix={<UserOutlined style={{ color: colors.textLight }} />}
                                                value={filters.utilisateurCodeId}
                                                onChange={(e) => setFilters(prev => ({ ...prev, utilisateurCodeId: e.target.value }))}
                                                style={{ width: 200 }}
                                                size="large"
                                                allowClear
                                            />

                                            <RangePicker
                                                format="YYYY-MM-DD"
                                                value={filters.dateRange}
                                                onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
                                                style={{ borderRadius: '8px' }}
                                                size="large"
                                            />

                                            <Select
                                                placeholder="Succès / Échec"
                                                style={{ width: 160 }}
                                                allowClear
                                                value={filters.success}
                                                onChange={(v) => setFilters(prev => ({ ...prev, success: v }))}
                                                size="large"
                                            >
                                                <Option value={true}>Succès</Option>
                                                <Option value={false}>Échec</Option>
                                            </Select>

                                            <Space>
                                                <Button
                                                    icon={<ReloadOutlined />}
                                                    size="large"
                                                    style={{ borderRadius: '8px', border: `1px solid ${colors.border}` }}
                                                    onClick={handleReset}
                                                >
                                                    Réinitialiser
                                                </Button>
                                                <Button
                                                    type="primary"
                                                    icon={<SearchOutlined />}
                                                    size="large"
                                                    style={{ background: colors.primary, borderColor: colors.primary, borderRadius: '8px' }}
                                                    onClick={handleSearch}
                                                    loading={loading}
                                                >
                                                    Filtrer
                                                </Button>
                                                <Dropdown
                                                    menu={{ items: exportMenuItems }}
                                                    trigger={['click']}
                                                    disabled={exporting || loading}
                                                >
                                                    <Button
                                                        icon={<DownloadOutlined />}
                                                        size="large"
                                                        style={{ borderRadius: '8px', border: `1px solid ${colors.border}` }}
                                                        loading={exporting}
                                                    >
                                                        Exporter
                                                    </Button>
                                                </Dropdown>
                                            </Space>
                                        </Space>
                                    </div>

                                    {/* Table */}
                                    <div style={{ padding: '0 40px 40px 40px' }}>
                                        {error && (
                                            <Alert
                                                type="error"
                                                message={error}
                                                showIcon
                                                style={{ marginBottom: 16, marginTop: 24 }}
                                            />
                                        )}
                                        <Spin spinning={loading}>
                                            <Table
                                                columns={columns}
                                                dataSource={logs}
                                                rowKey={(record) => record.id ?? (record.createdAt + record.actionType + record.username)}
                                                pagination={{
                                                    current: pagination.current,
                                                    pageSize: pagination.pageSize,
                                                    total: pagination.total,
                                                    showSizeChanger: true,
                                                    pageSizeOptions: ['20', '50', '100'],
                                                    showTotal: (total) => `${total} entrée(s)`,
                                                    style: { marginTop: '24px' },
                                                }}
                                                onChange={handleTableChange}
                                                style={{ background: colors.cardBg, marginTop: 24 }}
                                                locale={{
                                                    emptyText: loading ? 'Chargement...' : 'Aucune activité trouvée pour ces filtres',
                                                }}
                                                onRow={() => ({
                                                    style: { cursor: 'default', transition: 'all 0.2s ease' },
                                                    onMouseEnter: (e) => { e.currentTarget.style.background = `${colors.primary}05`; },
                                                    onMouseLeave: (e) => { e.currentTarget.style.background = 'transparent'; },
                                                })}
                                                scroll={{ x: 1100 }}
                                            />
                                        </Spin>
                                    </div>
                                </Card>
                            </div>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>
            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
};

export default ActivityLogs;
